import React, { useMemo, useState } from 'react';
import { GameState, Player } from '../../../domain/entities';
import { View } from '../../../App';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTriangleExclamation, faAmbulance, faComment, faFileContract, faThumbsUp, faTimes, faDatabase } from '@fortawesome/free-solid-svg-icons';
import { soundService } from '../../../services/SoundService';
import { getSquadSelectionSuggestions } from '../../../services/TeamService';
import { useStorageStatus } from '../../../hooks/useStorageStatus';
import './ActionAlertsPanel.css';

interface ActionAlertsPanelProps {
    gameState: GameState;
    pushView: (view: View) => void;
}

interface AlertItem {
    type: 'contract' | 'injury' | 'talk' | 'morale' | 'selection' | 'storage';
    count: number;
    label: string;
    icon: any;
    colorClass: string;
    actionLabel: string;
    onClick: () => void;
}

export const ActionAlertsPanel: React.FC<ActionAlertsPanelProps> = ({ gameState, pushView }) => {
    const [isDismissed, setIsDismissed] = useState(false);
    const { needsWarning: needsStorageWarning } = useStorageStatus();

    const alerts = useMemo(() => {
        const playerTeam = gameState.leagues[gameState.playerLeagueName]?.teams.find(
            t => t.name === gameState.playerTeamName
        );
        if (!playerTeam) return [];

        const items: AlertItem[] = [];

        // Storage warning for iOS devices without persistent storage
        if (needsStorageWarning) {
            items.push({
                type: 'storage',
                count: 1,
                label: 'Storage Not Protected',
                icon: faDatabase,
                colorClass: 'warning',
                actionLabel: 'Learn',
                onClick: () => {
                    soundService.playClick();
                    // Show a toast or alert about storage
                    alert('Your game data is on temporary storage. On iOS PWA apps, data can be cleared if your device runs low on storage. Consider using Safari instead of the home screen app for more persistent storage.');
                }
            });
        }

        // Expired contracts (CRITICAL - time sensitive)
        const expiredContracts = playerTeam.squad.filter(p => p.contract.yearsRemaining <= 0);
        if (expiredContracts.length > 0) {
            items.push({
                type: 'contract',
                count: expiredContracts.length,
                label: `Contract${expiredContracts.length > 1 ? 's' : ''}`,
                icon: faFileContract,
                colorClass: 'critical',
                actionLabel: 'Review',
                onClick: () => {
                    soundService.playClick();
                    pushView({ type: 'team', title: 'Squad', activeTab: 'morale' });
                }
            });
        }

        // Key player injuries
        const injuredPlayers = playerTeam.squad.filter(p => p.injury);
        if (injuredPlayers.length > 0) {
            items.push({
                type: 'injury',
                count: injuredPlayers.length,
                label: `Injur${injuredPlayers.length > 1 ? 'ies' : 'y'}`,
                icon: faAmbulance,
                colorClass: 'warning',
                actionLabel: 'View',
                onClick: () => {
                    soundService.playClick();
                    pushView({ type: 'team', title: 'Squad', activeTab: 'injuries' });
                }
            });
        }

        // Players wanting to talk
        const wantToTalk = playerTeam.squad.filter(p => p.wantsToTalk);
        if (wantToTalk.length > 0) {
            items.push({
                type: 'talk',
                count: wantToTalk.length,
                label: `Player${wantToTalk.length > 1 ? 's' : ''} Waiting`,
                icon: faComment,
                colorClass: 'warning',
                actionLabel: 'Talk',
                onClick: () => {
                    soundService.playClick();
                    pushView({ type: 'team', title: 'Squad', activeTab: 'morale' });
                }
            });
        }

        // High morale issues (count of unhappy/furious)
        const moraleConcerns = playerTeam.squad.filter(p => p.morale === 'Unhappy' || p.morale === 'Furious');
        if (moraleConcerns.length > 2) {
            items.push({
                type: 'morale',
                count: moraleConcerns.length,
                label: 'Morale Issues',
                icon: faTriangleExclamation,
                colorClass: 'warning',
                actionLabel: 'Manage',
                onClick: () => {
                    soundService.playClick();
                    pushView({ type: 'team', title: 'Squad', activeTab: 'morale' });
                }
            });
        }

        // Lineup suggestions (only if significant - raised to 4 to avoid suggestion fatigue)
        const suggestions = getSquadSelectionSuggestions(playerTeam, gameState);
        if (suggestions.length > 4) {
            items.push({
                type: 'selection',
                count: suggestions.length,
                label: 'Lineup Tips',
                icon: faThumbsUp,
                colorClass: 'info',
                actionLabel: 'Review',
                onClick: () => {
                    soundService.playClick();
                    pushView({ type: 'tactics', title: 'Tactics' });
                }
            });
        }

        return items;
    }, [gameState, pushView, needsStorageWarning]);

    if (alerts.length === 0 || isDismissed) {
        return null;
    }

    return (
        <div className="action-alerts-panel">
            <div className="alerts-container">
                <div className="alerts-icon-row">
                    <div>
                        <FontAwesomeIcon icon={faTriangleExclamation} className="alerts-header-icon" />
                        <span className="alerts-title">Action Required</span>
                    </div>

                    <button
                        className="alerts-dismiss-button"
                        onClick={() => setIsDismissed(true)}
                        title="Dismiss alerts for this session"
                    >
                        <FontAwesomeIcon icon={faTimes} />
                    </button>
                </div>

                <div className="alerts-items">
                    {alerts.map((alert, idx) => (
                        <div key={idx} className={`alert-item alert-${alert.colorClass}`}>
                            <div className="alert-content">
                                <FontAwesomeIcon icon={alert.icon} className="alert-icon" />
                                <div className="alert-text">
                                    <span className="alert-label">{alert.label}</span>
                                    <span className="alert-count">({alert.count})</span>
                                </div>
                            </div>
                            <button
                                className="alert-action-button"
                                onClick={alert.onClick}
                                title={`${alert.actionLabel} ${alert.label}`}
                            >
                                {alert.actionLabel}
                            </button>
                        </div>
                    ))}
                </div>
            </div>
        </div>
    );
};
